import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * The mounted provider's `push`, so code outside React can raise a toast.
 *
 * Two toast systems met in the integration merge: this provider (oubay, used by
 * the carpooling, events and restaurant screens through `useToast`) and a
 * module-level `showToast` (omar, called by `shared/api/global-error-handler.ts`
 * from React Query's cache, where no hook can run). They also collided on the
 * file name - `Toast.tsx` and `toast.tsx` are one file on Windows and macOS.
 * One file, one viewport: the module-level function forwards to the provider.
 */
let externalPush: ((message: string, type: ToastType) => void) | null = null;

export function showToast(
  message: string,
  opts: { variant?: Extract<ToastType, 'error' | 'info'>; durationMs?: number } = {},
): void {
  externalPush?.(message, opts.variant ?? 'error');
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // A toast's timer must not outlive the provider: it would set state on an unmounted tree.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { id, message, type }]);

      const timer = setTimeout(() => {
        timers.current.delete(timer);
        removeToast(id);
      }, 4500);
      timers.current.add(timer);
    },
    [removeToast],
  );

  useEffect(() => {
    externalPush = push;
    return () => {
      if (externalPush === push) externalPush = null;
    };
  }, [push]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const success = useCallback((msg: string) => push(msg, 'success'), [push]);
  const error = useCallback((msg: string) => push(msg, 'error'), [push]);
  const info = useCallback((msg: string) => push(msg, 'info'), [push]);
  const warning = useCallback((msg: string) => push(msg, 'warning'), [push]);

  return (
    <ToastContext.Provider value={{ showToast: push, success, error, info, warning }}>
      {children}
      {/* Toast viewport: responsive top-center on mobile, top-right on desktop */}
      <div
        aria-live="polite"
        className="fixed top-4 left-4 right-4 sm:left-auto sm:right-6 z-[9999] flex flex-col gap-2.5 pointer-events-none max-w-sm sm:w-full"
      >
        {toasts.map((t) => {
          /**
           * DESIGN.md §Status. Saturated `emerald-600` / `rose-600` on white is
           * a different product's palette: this one is "nurturing yet
           * professional" and deliberately does not shout. A toast is a status,
           * so it speaks the status language - the same four pairs as every
           * chip in the system.
           */
          const typeStyles: Record<ToastType, string> = {
            success: 'bg-status-success text-on-status-success',
            error: 'bg-error-container text-on-error-container',
            warning: 'bg-status-warning text-on-status-warning',
            info: 'bg-secondary-container text-on-secondary-fixed',
          };

          const typeIcons: Record<ToastType, string> = {
            success: 'check_circle',
            error: 'error',
            warning: 'warning',
            info: 'info',
          };

          return (
            <div
              key={t.id}
              role="alert"
              className={`pointer-events-auto flex items-start gap-3 p-4 rounded-3xl shadow-soft font-body-sm text-body-sm transition-[transform,box-shadow,opacity] duration-pop ease-out-strong ${
                typeStyles[t.type]
              }`}
            >
              <span className="material-symbols-outlined text-[20px] shrink-0 mt-0.5">
                {typeIcons[t.type]}
              </span>
              <div className="flex-1 leading-snug break-words">{t.message}</div>
              <button
                type="button"
                onClick={() => removeToast(t.id)}
                className="shrink-0 p-1 hover:opacity-80 transition-opacity rounded-lg"
                aria-label="Fermer la notification"
              >
                <span className="material-symbols-outlined text-[16px]">close</span>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
